import { IconCheckmark2Small } from 'central-icons/IconCheckmark2Small';
import { IconCircleInfo } from 'central-icons/IconCircleInfo';
import { useEffect, useState } from 'react';

import { CenterLoading } from '@revelio/core';
import type { CustomRoleTaxonomySelection } from '@revelio/data-access';
import { TaxonomyType } from '@revelio/data-access';
import { MAX_PRIMARY_ENTITY_SELECTIONS } from '@revelio/iso-utility';
import {
  Dialog,
  RailModalBody,
  RailModalCloseButton,
  RailModalContent,
  RailModalDivider,
  RailModalFooter,
  RailModalHeader,
  RailModalPane,
  Text,
  Button as UiButton,
} from '@revelio/ui';

import { DataProviderQueryStatus } from '../../next/CustomDash/components/DataProvider/types';
import { useCurrentReferenceTaxonomyVersion } from '../selection-lists/hooks/useReferenceTaxonomyVersions';
import { useCurrentRoleTaxonomyVersion } from '../selection-lists/hooks/useRoleTaxonomyAdapter';
import { useCanSavePreferences } from './hooks/useCanSavePreferences';
import { usePreferenceToast } from './hooks/usePreferenceToast';
import { useUserPreferences } from './hooks/useUserPreferences';
import { PreferencesRail } from './preference-editors/PreferencesRail';
import type {
  OnPreferenceUpdateCallback,
  preferenceEditors,
} from './preference-editors/preferenceEditors';
import styles from './preferences-modal.module.css';
import {
  PAGE_ORDER_SCOPES,
  PreferenceScope,
  SCOPED_PREFERENCE_KEYS,
  SELF_MANAGED_PREFERENCE_KEYS,
  UserPreferenceKey,
  UserPreferenceSubKey,
  type UserPreferences,
} from './types';
import { UserPreferencesDataProvider } from './UserPreferencesDataProvider';
import { deriveEffectiveTaxonomiesForScope } from './utils/derive-effective-taxonomies';
import { calculateVisiblePreferenceDiff } from './utils/preference-diff';
import {
  restoreVisiblePreferencesToDefaults,
  saveVisiblePreferencesAsDefault,
} from './utils/preference-operations';
import {
  getVisiblePreferenceEditors,
  getVisiblePreferenceKeys,
} from './utils/visibility';

/** Convenience types derived from registry */
type PreferenceEntry = (typeof preferenceEditors)[number];
type PreferenceId = PreferenceEntry['meta']['id'];

interface PreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  preferencesFromFilters?: Partial<UserPreferences>;
  onPreferenceUpdate: OnPreferenceUpdateCallback;
  affectedScope: PreferenceScope;
  preferenceVisibility?: (key: UserPreferenceKey) => boolean;
}

export const PreferencesModal = ({
  affectedScope,
  isOpen,
  onClose,
  onPreferenceUpdate,
  preferencesFromFilters,
  preferenceVisibility,
}: PreferencesModalProps) => {
  const [activeId, setActiveId] = useState<PreferenceId>();
  const [isSaving, setIsSaving] = useState(false);
  const canSave = useCanSavePreferences();
  const showToast = usePreferenceToast();
  const { getSpecificTaxonomyVersion: getRoleTaxonomyVersion } =
    useCurrentRoleTaxonomyVersion();
  const { version: currentSkillTaxonomyVersion } =
    useCurrentReferenceTaxonomyVersion({ taxonomyType: TaxonomyType.Skill });
  const { version: currentActivityTaxonomyVersion } =
    useCurrentReferenceTaxonomyVersion({ taxonomyType: TaxonomyType.Activity });

  // The scoped section (Default Filters, Analytics Tabs) can edit any primary
  // scope from any page via the switcher; default to the page's own scope when
  // it is one of them, else Company (Talent Discovery / home / global).
  const initialScope = PAGE_ORDER_SCOPES.includes(affectedScope)
    ? affectedScope
    : PreferenceScope.COMPANY;
  const [selectedScope, setSelectedScope] =
    useState<PreferenceScope>(initialScope);
  // Scoped keys read/write the switcher's scope, independent of the page.
  // Same react-query cache as the page provider — no extra fetch.
  const scopedPrefs = useUserPreferences(selectedScope);

  const isScoped = (key: UserPreferenceKey) =>
    SCOPED_PREFERENCE_KEYS.includes(key);

  const effectivePreferenceVisibility = (key: UserPreferenceKey): boolean => {
    // Scoped settings are always offered (the switcher supplies the scope), so
    // they no longer depend on the page's own scope. Flag/page gates from the
    // caller still apply.
    return preferenceVisibility ? preferenceVisibility(key) : true;
  };

  const relevantEditors = getVisiblePreferenceEditors(
    effectivePreferenceVisibility
  );
  const visiblePreferenceKeys = getVisiblePreferenceKeys(
    effectivePreferenceVisibility
  );
  const activeEntry =
    relevantEditors.find((e) => e.meta.id === activeId) ?? relevantEditors[0];
  const ActiveEditorComponent = activeEntry?.component;

  useEffect(() => {
    setActiveId(activeEntry?.meta.id ?? undefined);
  }, [activeEntry]);

  // Handle setting current preferences as default
  const handleSetAsDefault = async (
    effectivePreferences: UserPreferences,
    changedKeys: UserPreferenceKey[],
    refetchSavedPreferences: () => Promise<void>
  ) => {
    try {
      setIsSaving(true);
      // The version stamp is consumed by ROLE-page defaults reconciliation, so
      // it must always describe the ROLE-scope-derived selection (generic when
      // the global choice is adaptive) — never a company-page adaptive
      // resolution, which would clobber the stamp for every other scope.
      const roleSelectionForStamp: CustomRoleTaxonomySelection =
        (deriveEffectiveTaxonomiesForScope(
          effectivePreferences.taxonomies,
          PreferenceScope.ROLE
        )?.role as CustomRoleTaxonomySelection) ?? { default: true };
      // Persist only the keys that changed. Scoped keys route to the switcher's
      // scope; globals to GLOBAL — the passed prefs carry global values for the
      // page and scoped values for `selectedScope`, and `filterPreferencesForScope`
      // splits them.
      await saveVisiblePreferencesAsDefault(
        selectedScope,
        effectivePreferences,
        changedKeys,
        refetchSavedPreferences,
        {
          role: getRoleTaxonomyVersion(roleSelectionForStamp),
          skill: currentSkillTaxonomyVersion,
          activity: currentActivityTaxonomyVersion,
        }
      );
      showToast('Preferences saved', 'success', {
        description: 'Your current preferences have been set as default.',
      });
    } catch (err) {
      showToast('Failed to save preferences', 'error', { error: err });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle resetting to defaults
  const handleRestoreDefaults = async (savedDefaults: UserPreferences) => {
    try {
      const { appliedDefaults } = restoreVisiblePreferencesToDefaults(
        selectedScope,
        savedDefaults,
        visiblePreferenceKeys
      );

      // Update local filters to reflect restored defaults for visible preferences only.
      // Self-managed prefs (e.g. PageOrder) handle their own reset and bypass this flow.
      visiblePreferenceKeys.forEach((key) => {
        if (SELF_MANAGED_PREFERENCE_KEYS.includes(key)) return;
        // A scoped restore for a scope other than the page's own must not push
        // into the current page's live filters — it's not this page's scope.
        if (isScoped(key) && selectedScope !== affectedScope) return;
        const value = appliedDefaults[key];
        onPreferenceUpdate(key as UserPreferenceKey, value);
      });

      showToast('Preferences restored', 'success', {
        description: 'Your preferences have been restored to defaults.',
      });
    } catch (err) {
      showToast('Failed to reset preferences', 'error', { error: err });
    }
  };

  return (
    <UserPreferencesDataProvider
      affectedScope={affectedScope}
      preferencesFromFilters={preferencesFromFilters}
    >
      {({ data, status }) => {
        if (!data) return null;
        const { refetchSavedPreferences } = data;
        if (!data.preferences || !data.savedPreferences) return null;
        const hasError = status === DataProviderQueryStatus.Error;
        const isFetching = status === DataProviderQueryStatus.Fetching;

        // Global keys come from the page's provider (its merge includes GLOBAL);
        // scoped keys come from the switcher's selected scope. Combine so diff,
        // display and save all reason over one object.
        const preferences: UserPreferences = { ...data.preferences };
        const savedPreferences: UserPreferences = { ...data.savedPreferences };
        const mutablePrefs = preferences as Record<string, unknown>;
        const mutableSaved = savedPreferences as Record<string, unknown>;
        for (const key of SCOPED_PREFERENCE_KEYS) {
          mutablePrefs[key] = scopedPrefs.preferences[key];
          mutableSaved[key] = scopedPrefs.savedPreferences[key];
        }

        const diff = calculateVisiblePreferenceDiff(
          preferences,
          savedPreferences,
          visiblePreferenceKeys,
          hasError
        );

        // The storage keys "Set as default" should persist: the changed keys
        // only, with taxonomy sub-keys folded back into the Taxonomies key.
        const taxonomySubKeys: (UserPreferenceKey | UserPreferenceSubKey)[] =
          Object.values(UserPreferenceSubKey);
        const changedKeys = Array.from(
          new Set(
            diff.map((d) =>
              taxonomySubKeys.includes(d)
                ? UserPreferenceKey.Taxonomies
                : (d as UserPreferenceKey)
            )
          )
        );
        const hasInvalidDefaultFilters =
          preferences[UserPreferenceKey.DefaultFilters].length >
          MAX_PRIMARY_ENTITY_SELECTIONS;

        // Themed footer actions are the @revelio/ui ghost text buttons (the
        // closest DS variant to the mock's plain text button).
        const setAsDefaultButton = (
          <UiButton
            data-testid="preferences-set-as-default"
            variant="ghost"
            size="sm"
            className={styles.footerAction}
            onClick={() =>
              handleSetAsDefault(
                preferences,
                changedKeys,
                refetchSavedPreferences
              )
            }
            loading={isSaving}
            disabled={hasInvalidDefaultFilters}
          >
            Set as default
          </UiButton>
        );
        const restoreDefaultsButton = (
          <UiButton
            data-testid="preferences-restore-defaults"
            variant="ghost"
            size="sm"
            className={styles.footerAction}
            onClick={() => handleRestoreDefaults(savedPreferences)}
          >
            Reset
          </UiButton>
        );

        const statusContent = (() => {
          if (hasError) {
            return (
              <div className={styles.loadErrorStatus}>
                <IconCircleInfo size="1rem" className={styles.statusIcon} />
                <Text fontSize="sm" className={styles.loadErrorText}>
                  We couldn't load your saved defaults. You can still adjust
                  preferences locally.
                </Text>
              </div>
            );
          }
          if (diff.length === 0) {
            return (
              <div className={styles.matchStatus}>
                <IconCheckmark2Small size="1rem" className={styles.matchIcon} />
                <Text fontSize="sm">
                  Dashboard settings match your default settings
                </Text>
              </div>
            );
          }

          const effectiveDiff = diff.filter(
            (d) => d !== UserPreferenceKey.Taxonomies
          );
          const differenceText =
            effectiveDiff.length === 1
              ? '1 setting differs from your account defaults'
              : `${effectiveDiff.length} settings differ from your account defaults`;
          return (
            <div className={styles.diffBar}>
              <div className={styles.diffText}>
                <Text fontSize="sm">{differenceText}</Text>
              </div>

              <div className={styles.diffActions}>
                {restoreDefaultsButton}
                {canSave ? (
                  setAsDefaultButton
                ) : (
                  <Text
                    fontSize="sm"
                    tone="muted"
                    data-testid="preferences-read-only-hint"
                  >
                    Read-only while impersonating
                  </Text>
                )}
              </div>
            </div>
          );
        })();

        const activeEditor = ActiveEditorComponent && activeEntry && (
          <ActiveEditorComponent
            affectedScope={
              isScoped(activeEntry.meta.id) ? selectedScope : affectedScope
            }
            onPreferenceUpdate={onPreferenceUpdate}
            preferences={preferences}
            diff={diff}
          />
        );

        return (
          <Dialog
            open={isOpen}
            onClose={onClose}
            size="2xl"
            closeButton={false}
            className={styles.content}
            bodyClassName={styles.dialogBody}
            // The rail shell owns the chrome, so there is no `title` for the
            // dialog to derive `aria-labelledby` from.
            aria-label="Preferences"
          >
            <RailModalContent>
              <RailModalHeader>Preferences</RailModalHeader>
              <RailModalCloseButton onClick={onClose} />
              <RailModalDivider />
              <RailModalBody className={styles.modalBody}>
                {isFetching ? (
                  <div className={styles.loading}>
                    <CenterLoading />
                  </div>
                ) : (
                  <>
                    <PreferencesRail
                      editors={relevantEditors}
                      activeId={activeId}
                      onSelect={setActiveId}
                      diff={diff}
                      hasError={hasError}
                      scopeOptions={PAGE_ORDER_SCOPES}
                      selectedScope={selectedScope}
                      onScopeChange={setSelectedScope}
                    />
                    <RailModalPane className={styles.editorPane}>
                      {activeEditor}
                    </RailModalPane>
                  </>
                )}
              </RailModalBody>
              <RailModalDivider />
              <RailModalFooter className={styles.footer}>
                {statusContent}
              </RailModalFooter>
            </RailModalContent>
          </Dialog>
        );
      }}
    </UserPreferencesDataProvider>
  );
};
